import React from "react";
import { CKEditor } from "ckeditor4-react";

export const CKEDITOR_URL = `${process.env.REACT_APP_API_URL}/staticpra/ckeditor/ckeditor.js`;

// The hosted build bundles the Export to PDF plugin, which needs a paid
// `exportPdf_tokenUrl` and logs `exportpdf-no-token-url` on every instance.
const REMOVED_PLUGINS = ["exportpdf"];

function mergeRemovePlugins(value) {
    const existing = Array.isArray(value)
        ? value
        : typeof value === "string"
            ? value.split(",")
            : [];
    return [...new Set([...existing.map((p) => p.trim()).filter(Boolean), ...REMOVED_PLUGINS])].join(",");
}

function isElementInDocument(element) {
    const node = element && element.$;
    if (!node) return false;
    return typeof node.isConnected === "boolean"
        ? node.isConnected
        : node.ownerDocument.documentElement.contains(node);
}

/**
 * ckeditor4-react starts CKEDITOR.replace() asynchronously. When the React host
 * is unmounted (key change, conditional render) before CKEditor finishes loading,
 * CKEditor still tries to build its UI next to the removed element and throws
 * `Cannot read properties of null (reading 'insertBefore')`. CKEditor 4.16's own
 * `isDetached()` check only looks at the not-yet-created container, so it misses
 * this case. Stop the UI build for orphaned instances and destroy them instead.
 */
function installDetachedGuard(CKEDITOR) {
    if (!CKEDITOR || CKEDITOR.__praDetachedGuard) return;
    CKEDITOR.__praDetachedGuard = true;

    CKEDITOR.on("instanceCreated", (createdEvt) => {
        const editor = createdEvt.editor;

        editor.on(
            "loaded",
            (loadedEvt) => {
                if (isElementInDocument(editor.element)) return;

                // Priority 1 runs before the themed UI listener; cancelling skips it.
                loadedEvt.cancel();
                setTimeout(() => {
                    try {
                        if (!editor.isDestroyed()) editor.destroy(true);
                    } catch (err) {
                        // Instance never finished initialising; nothing left to clean up.
                    }
                }, 0);
            },
            null,
            null,
            1
        );
    });
}

export default function SafeCKEditor({ config, editorUrl = CKEDITOR_URL, onBeforeLoad, ...props }) {
    const safeConfig = {
        ...config,
        removePlugins: mergeRemovePlugins(config && config.removePlugins),
    };

    return (
        <CKEditor
            {...props}
            editorUrl={editorUrl}
            config={safeConfig}
            onBeforeLoad={(CKEDITOR) => {
                installDetachedGuard(CKEDITOR);
                if (onBeforeLoad) onBeforeLoad(CKEDITOR);
            }}
        />
    );
}
